"use client";

import React, { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef } from 'react';

declare global {
    interface Window {
        turnstile?: {
            render: (container: HTMLElement, options: {
                sitekey: string;
                action?: string;
                theme?: 'auto' | 'light' | 'dark';
                size?: 'normal' | 'compact' | 'flexible';
                appearance?: 'always' | 'execute' | 'interaction-only';
                callback?: (token: string) => void;
                'expired-callback'?: () => void;
                'error-callback'?: () => void;
            }) => string;
            remove: (widgetId: string) => void;
            reset: (widgetId?: string) => void;
        };
    }
}

type TurnstileWidgetProps = {
    action: string;
    onTokenChange: (token: string) => void;
    theme?: 'auto' | 'light' | 'dark';
    size?: 'normal' | 'compact' | 'flexible';
};

export type TurnstileWidgetHandle = {
    reset: () => void;
};

const TURNSTILE_SCRIPT_ID = 'cf-turnstile-script';
const DEFAULT_SITE_KEY = '0x4AAAAAADB-fkvvboqd_Fkt';
//const DEFAULT_SITE_KEY = '1x00000000000000000000AA';

const TurnstileWidget = forwardRef<TurnstileWidgetHandle, TurnstileWidgetProps>(function TurnstileWidget(
    { action, onTokenChange, theme = 'auto', size = 'flexible' },
    ref,
) {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const widgetIdRef = useRef<string | null>(null);
    const onTokenChangeRef = useRef(onTokenChange);

    // Keep the latest callback in a ref so the render effect doesn't re-run
    // every time the parent passes a new inline function reference.
    useLayoutEffect(() => {
        onTokenChangeRef.current = onTokenChange;
    }, [onTokenChange]);

    useImperativeHandle(ref, () => ({
        reset: () => {
            if (window.turnstile && widgetIdRef.current) {
                window.turnstile.reset(widgetIdRef.current);
                onTokenChangeRef.current('');
            }
        },
    }), []);

    useEffect(() => {
        const siteKey = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || DEFAULT_SITE_KEY;

        if (!siteKey || !containerRef.current) {
            return;
        }

        onTokenChangeRef.current('');

        const renderWidget = () => {
            if (!window.turnstile || !containerRef.current) {
                return;
            }

            widgetIdRef.current = window.turnstile.render(containerRef.current, {
                sitekey: siteKey,
                action,
                theme,
                size,
                callback: (token: string) => onTokenChangeRef.current(token),
                'expired-callback': () => onTokenChangeRef.current(''),
                'error-callback': () => onTokenChangeRef.current(''),
            });
        };

        const existingScript = document.getElementById(TURNSTILE_SCRIPT_ID) as HTMLScriptElement | null;
        if (window.turnstile) {
            renderWidget();
        } else if (existingScript) {
            existingScript.addEventListener('load', renderWidget, { once: true });
        } else {
            const script = document.createElement('script');
            script.id = TURNSTILE_SCRIPT_ID;
            script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
            script.async = true;
            script.defer = true;
            script.addEventListener('load', renderWidget, { once: true });
            document.head.appendChild(script);
        }

        return () => {
            if (window.turnstile && widgetIdRef.current) {
                window.turnstile.remove(widgetIdRef.current);
            }
            widgetIdRef.current = null;
        };
    }, [action, theme, size]);

    return <div ref={containerRef} className="cf-turnstile-container" />;
});

export default TurnstileWidget;
